import { cellCenter } from '../grid.ts';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, DrillsRules, DrillsState, Hero, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { pulseLand } from './brasa.ts';
import { barrenaFresh, barrenaTick } from './barrena.ts';
import { shootTick } from './workshop.ts';
import { DRILLS } from './ojos-layout.ts';

/**
 * The drills (docs/sendas/artifice.md, chapter 4, act 3, the test; §4, puzzle 4, «Los taladros»; the ground in
 * battle/ojos-layout.ts). The crystal of Tormo's eyes is in a geode inside a block of ice; its north and south faces
 * are the two walls. The mill at the mouth gives `mill` (3) of force to a shaft along the roof; belts come down from it
 * to four drills, two by each face, each with its clutch (in or out), its force (1 or 2) painted on it and an arrow:
 * the way it cuts. Two levers at the belts' station turn a belt round: I that of drills 1 and 2, II that of 3 and 4.
 *
 * Drills in asking more than the mill's 3 jam the shaft at once: the jolt throws every clutch out, shakes the hall
 * (`jamBp` of the hero's life) and frees a nightmare frozen in the ice. A drill turning against its arrow doesn't cut,
 * but asks its force all the same. A wall falls after `cut` (6 s) cut with its whole force (3): its two drills in,
 * right, and turning (a drill La Barrena reached stops `stall`, and that wall's cut starts over). So the obvious
 * (everything in) jams, and drills put in a few at a time with no eye on the belts leave two turning backwards; the
 * way is one wall's two drills right and nothing else in, then out, the belts turned for the other's.
 *
 * A tap on a drill or a lever sends the hero; standing still by it `clutch` (a lever: `lever`) works it. The roof sheds
 * ice (`pulse`: one on what the hero works, else on it; one on a drill in), frozen ones come out of the ice now and then
 * (`foes`), La Barrena (battle/barrena.ts) for the drills that turn; Dunna's bolts. Won with both walls down and La
 * Barrena down; lost with Dunna or the hero down.
 *
 * Cost: four drills and two walls added up once a tick; the boss's.
 */

/** The marks: drills 0–3 (their clutches), the levers 4 (I) and 5 (II). */
export const DRILL_LEVERS = DRILLS;

const fresh = (r: DrillsRules): DrillsState => ({
  inn: r.drills.map(() => false),
  belts: [0, 0],
  stall: r.drills.map(() => 0),
  job: -1,
  held: 0,
  cut: [0, 0],
  down: [-1, -1],
  jams: 0,
  pulls: 0,
  wrong: false,
  pulse: r.pulse.first,
  spots: [],
  foe: r.foes.first,
  bz: barrenaFresh(r),
  shot: 0,
});

/** Whether drill `d` turns its arrow's way now. */
export const drillTurnsRight = (r: DrillsRules, w: DrillsState, d: number): boolean => (r.drills[d]?.right0 ?? false) !== (w.belts[d < 2 ? 0 : 1] === 1);

/** The force the drills in ask of the mill. */
export const drillsAsk = (r: DrillsRules, w: DrillsState): number => r.drills.reduce((a, x, d) => a + (w.inn[d] ? x.force : 0), 0);

/** The force cutting a wall now (its drills in, right, turning). */
export const wallForce = (s: BattleState, r: DrillsRules, w: DrillsState, wall: number): number =>
  r.drills.reduce((a, x, d) => a + (x.wall === wall && w.inn[d] && s.tick >= (w.stall[d] as number) && drillTurnsRight(r, w, d) ? x.force : 0), 0);

/** A mark's cell: a drill's clutch spot, a lever. */
export const drillCell = (r: DrillsRules, at: number): number => (at < DRILLS ? (r.drills[at]?.spot as number) : (r.levers[at - DRILLS] as number));

/** A tap on a drill or a lever: the hero goes to it. */
export const drillOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.drills;
  const w = s.senda?.drills;
  if (!r || !w || at < 0 || at > DRILLS + 1 || (w.down[0] as number) >= 0 && (w.down[1] as number) >= 0) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, drillCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

/** The shaft jams: every clutch out, the hall shaken, something out of the ice. */
const jam = (s: BattleState, r: DrillsRules, w: DrillsState, cue: (c: string) => void): void => {
  w.jams++;
  for (let d = 0; d < w.inn.length; d++) w.inn[d] = false;
  const at = r.ice[rngInt(s.rng, r.ice.length)] as { x: number; y: number };
  ev(s, { t: 'senda', kind: 'jam', npc: '', x: at.x, y: at.y, n: w.jams });
  for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.jamBp), null, 'physical', true);
  thaw(s, at, 1);
  cue('jam');
};

/** A frozen nightmare out of the ice. */
const thaw = (s: BattleState, at: { x: number; y: number }, n: number): void => {
  for (let k = 0; k < n; k++) {
    const e = spawnEnemy(s, rngInt(s.rng, 2) === 0 ? 'wraith' : 'skeleton', 1, false, 0, at.x + rngRange(s.rng, -300, 300), at.y + rngRange(s.rng, -200, 200));
    if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  }
  ev(s, { t: 'senda', kind: 'thaw', npc: '', x: at.x, y: at.y, n });
};

const heroWork = (s: BattleState, r: DrillsRules, w: DrillsState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, drillCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  const lever = w.job >= DRILLS;
  if (++w.held < (lever ? r.lever : r.clutch)) return;
  const at = w.job;
  w.job = -1;
  w.held = 0;
  if (lever) {
    const b = at - DRILLS;
    w.belts[b] = 1 - (w.belts[b] as number);
    w.pulls++;
    ev(s, { t: 'senda', kind: 'belt', npc: '', x: c.x, y: c.y, n: b });
    return;
  }
  const d = at;
  const x = r.drills[d];
  if (!x) return;
  if (w.inn[d]) {
    w.inn[d] = false;
    ev(s, { t: 'senda', kind: 'clutchOut', npc: '', x: x.at.x, y: x.at.y, n: d });
    return;
  }
  w.inn[d] = true;
  ev(s, { t: 'senda', kind: 'clutchIn', npc: '', x: x.at.x, y: x.at.y, n: d });
  if (drillsAsk(r, w) > r.mill) jam(s, r, w, cue);
};

/** The walls: cut while their whole force is on them; down after `cut`. */
const cutTick = (s: BattleState, r: DrillsRules, w: DrillsState, cue: (c: string) => void): void => {
  for (let wall = 0; wall < 2; wall++) {
    if ((w.down[wall] as number) >= 0) continue;
    if (wallForce(s, r, w, wall) >= r.mill) w.cut[wall] = (w.cut[wall] as number) + 1;
    else w.cut[wall] = 0;
    if ((w.cut[wall] as number) < r.cut) continue;
    w.down[wall] = s.tick;
    const n = w.down.filter((x) => x >= 0).length;
    const p = r.walls[wall] as { x: number; y: number };
    ev(s, { t: 'senda', kind: 'wallDown', npc: '', x: p.x, y: p.y, n: wall });
    cue(`wall${n}`);
  }
  // A drill in turning backwards at a wall still standing: said once.
  if (!w.wrong && r.drills.some((x, d) => w.inn[d] && (w.down[x.wall] as number) < 0 && !drillTurnsRight(r, w, d))) {
    w.wrong = true;
    cue('wrong');
  }
};

/** Ice off the roof: one on what the hero works (else on it), one on a drill in. */
const pulse = (s: BattleState, r: DrillsRules, w: DrillsState): void => {
  if (s.waveT < w.pulse) return;
  w.pulse = s.waveT + r.pulse.every;
  const t = s.tick + r.pulse.warn;
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.pulse.warn });
  };
  const h = s.heroes.find((x) => x.downTicks === 0);
  const job = w.job >= 0 ? cellCenter(s.map.radius, drillCell(r, w.job)) : null;
  if (job) add(job.x + rngRange(s.rng, -200, 200), job.y + rngRange(s.rng, -200, 200));
  else if (h) add(h.x + rngRange(s.rng, -300, 300), h.y + rngRange(s.rng, -300, 300));
  const ins = r.drills.map((_, d) => d).filter((d) => w.inn[d]);
  if (ins.length) {
    const d = ins[rngInt(s.rng, ins.length)] as number;
    const c = cellCenter(s.map.radius, r.drills[d]?.spot as number);
    const first = w.spots[w.spots.length - 1];
    if (!first || first.t !== t || !withinRange(first.x, first.y, c.x, c.y, r.pulse.r * 2)) add(c.x, c.y);
  }
};

export const drillsTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.drills;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.drills ??= fresh(r));
  if (!s.started) return;
  barrenaTick(s, r, w, cue);
  if ((w.down[0] as number) < 0 || (w.down[1] as number) < 0) heroWork(s, r, w, cue);
  cutTick(s, r, w, cue);
  pulse(s, r, w);
  pulseLand(s, w.spots, r.pulse);
  if (s.waveT >= w.foe) {
    w.foe = s.waveT + r.foes.every;
    thaw(s, r.ice[rngInt(s.rng, r.ice.length)] as { x: number; y: number }, r.foes.n);
  }
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** Both walls down and La Barrena down: three steps. */
export const drillsGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.drills;
  if (!w) return { done: 0, need: 3 };
  return { done: w.down.filter((x) => x >= 0).length + (w.bz.down >= 0 ? 1 : 0), need: 3 };
};

export const drillsResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.drills;
  if (!w) return null;
  const a = w.down[0] as number;
  const b = w.down[1] as number;
  if (a < 0 || b < 0 || w.bz.down < 0) return null;
  const last = a > b ? (a > w.bz.down ? a : w.bz.down) : b > w.bz.down ? b : w.bz.down;
  return s.tick >= last + 24 ? 'won' : null;
};
